import { SelectCard } from 'components/Form/SelectCards/SelectCard';
import { SelectCards } from 'components/Form/SelectCards';
import React, { useCallback, useEffect, useState } from 'react';
import 'twin.macro';
import { StepWrapper } from 'containers/Demarrage/components/StepWrapper';
import { UserRole } from 'containers/Demarrage/types';
import { useUpdateDemarrage } from 'containers/Demarrage/queries/useUpdateDemarrage';
import { useDemarrage } from 'containers/Demarrage/queries/useDemarrage';
import { Trans, useTranslation } from 'react-i18next';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import Input from 'components/Form/Input2';
import _debounce from 'lodash-es/debounce';
import FormGroup from 'components/Form/FormGroup';
import { useRolesForScholarSystem } from 'containers/scholarSystem';

export function Role() {
  const update = useUpdateDemarrage();
  const { t } = useTranslation(['onboarding']);
  const user = useUser();
  const { data: demarrageData, isLoading } = useDemarrage();

  const scholarSystemValue =
    demarrageData?.scholarSystem ?? user?.scholarSystem ?? 'FR';

  const roles = useRolesForScholarSystem(scholarSystemValue, v => t(v));

  const [otherRoleValue, setOtherRoleValue] = useState<string>(
    demarrageData?.otherRoleDetails || '',
  );

  const debouncedSave = useCallback(
    _debounce(nextValue => update({ otherRoleDetails: nextValue }), 400),
    [update],
  );

  const handleOtherRoleChange = (nextValue: string) => {
    setOtherRoleValue(nextValue);
    debouncedSave(nextValue);
  };

  function handleBeforeNext() {
    debouncedSave.cancel();

    if (otherRoleValue !== demarrageData?.otherRoleDetails) {
      return update({ otherRoleDetails: otherRoleValue });
    }
    return;
  }

  useEffect(() => {
    if (demarrageData && (demarrageData.userRoles || []).length === 0) {
      const defaultRole = roles[0]?.value || '1er degré';
      update({ userRoles: [defaultRole as UserRole], roles: ['teacher'] });
    }
  }, []);

  if (!demarrageData || isLoading) {
    return null;
  }

  const userRoles = demarrageData?.userRoles || [];

  const isNextDisabled =
    userRoles.length === 0 ||
    (userRoles.includes('autre') && !otherRoleValue.trim());

  return (
    <StepWrapper
      currentStep="role"
      nextDisabled={isNextDisabled}
      title={t('onboarding:role.title')}
      onNext={handleBeforeNext}
    >
      <div tw="flex flex-col gap-2">
        <SelectCards<UserRole, true>
          value={userRoles}
          onChange={async userRolesValues => {
            await update({
              userRoles: userRolesValues,
              roles: userRolesValues.includes('directeur') ? [] : ['teacher'], // reset roles if director is checked, or set teacher role otherwise (because the step is skipped)
            });
          }}
          options={roles.map(r => ({
            label: r.label,
            value: r.value,
          }))}
          size="big"
          direction="vertical"
          isMulti
        />
        <SelectCard
          onChange={async () => {
            await update({
              userRoles: userRoles.includes('autre')
                ? userRoles.filter(r => r !== 'autre')
                : [...userRoles, 'autre'],
              otherRoleDetails: undefined, // reset when we check or uncheck
            });
            setOtherRoleValue(''); // reset local state too
          }}
          id="autre-role-checkbox"
          size="big"
          option={{
            label: (
              <Trans
                i18nKey={'onboarding:role.other'}
                components={{ bold: <span tw="font-semibold" /> }}
              />
            ),
            description: userRoles.includes('autre') && (
              <FormGroup
                label={t('onboarding:role.otherAddDetails')}
                htmlFor="other-role-input"
              >
                <Input
                  id="other-role-input"
                  autoFocus
                  value={otherRoleValue}
                  onChange={e => handleOtherRoleChange(e?.currentTarget.value)}
                />
              </FormGroup>
            ),
            value: 'autre',
          }}
          checked={userRoles.includes('autre')}
          isMulti
        />
      </div>
    </StepWrapper>
  );
}
